import { create } from 'zustand';
import type { ContractStatus } from '@shared/types';
import { useShellStore } from '@renderer/app/state/shell-store';
import { countHiddenSelectedIds, removeSelectedId, toggleSelectionIds } from '@renderer/shared/utils/collection-selection';

export type ContractsSort = 'recent' | 'effective' | 'title';
export type ContractEditorTab = 'general' | 'document' | 'activity';

interface ContractsSelectionSession {
  selectedIds: string[];
  anchorId: string | null;
  queryKey: string;
  hiddenSelectedCount: number;
}

function createInitialSelectionSession(): ContractsSelectionSession {
  return {
    selectedIds: [],
    anchorId: null,
    queryKey: '',
    hiddenSelectedCount: 0
  };
}

interface ContractsViewStore {
  statusFilter: 'all' | ContractStatus;
  customerFilter: 'all' | string;
  projectFilter: 'all' | string;
  sort: ContractsSort;
  editorTab: ContractEditorTab;
  selectionSession: ContractsSelectionSession;
  setStatusFilter: (value: 'all' | ContractStatus) => void;
  setCustomerFilter: (value: 'all' | string) => void;
  setProjectFilter: (value: 'all' | string) => void;
  setSort: (value: ContractsSort) => void;
  setEditorTab: (value: ContractEditorTab) => void;
  setViewMode: (value: 'grid' | 'list') => Promise<void>;
  syncSelectionQuery: (queryKey: string, visibleIds: string[]) => void;
  toggleSelection: (
    itemId: string,
    orderedIds: string[],
    queryKey: string,
    options?: {
      additive?: boolean;
      range?: boolean;
    }
  ) => void;
  removeSelectedId: (itemId: string, visibleIds: string[]) => void;
  clearSelection: () => void;
}

export const useContractsViewStore = create<ContractsViewStore>((set) => ({
  statusFilter: 'all',
  customerFilter: 'all',
  projectFilter: 'all',
  sort: 'recent',
  editorTab: 'general',
  selectionSession: createInitialSelectionSession(),
  setStatusFilter: (statusFilter) => set({ statusFilter }),
  setCustomerFilter: (customerFilter) => set({ customerFilter }),
  setProjectFilter: (projectFilter) => set({ projectFilter }),
  setSort: (sort) => set({ sort }),
  setEditorTab: (editorTab) => set({ editorTab }),
  setViewMode: async (contractsViewMode) => {
    await useShellStore.getState().savePreferences({ contractsViewMode });
  },
  syncSelectionQuery: (queryKey, visibleIds) =>
    set((state) => {
      const hiddenSelectedCount = countHiddenSelectedIds(state.selectionSession.selectedIds, visibleIds);

      if (state.selectionSession.queryKey === queryKey && state.selectionSession.hiddenSelectedCount === hiddenSelectedCount) {
        return state;
      }

      return {
        selectionSession: {
          ...state.selectionSession,
          queryKey,
          hiddenSelectedCount
        }
      };
    }),
  toggleSelection: (itemId, orderedIds, queryKey, options) =>
    set((state) => {
      const nextSelection = toggleSelectionIds({
        selectedIds: state.selectionSession.selectedIds,
        anchorId: state.selectionSession.anchorId,
        itemId,
        orderedIds,
        additive: options?.additive ?? false,
        range: options?.range ?? false
      });

      return {
        selectionSession: {
          ...state.selectionSession,
          ...nextSelection,
          queryKey,
          hiddenSelectedCount: countHiddenSelectedIds(nextSelection.selectedIds, orderedIds)
        }
      };
    }),
  removeSelectedId: (itemId, visibleIds) =>
    set((state) => {
      const selectedIds = removeSelectedId(state.selectionSession.selectedIds, itemId);

      return {
        selectionSession: {
          ...state.selectionSession,
          selectedIds,
          anchorId: state.selectionSession.anchorId === itemId ? null : state.selectionSession.anchorId,
          hiddenSelectedCount: countHiddenSelectedIds(selectedIds, visibleIds)
        }
      };
    }),
  clearSelection: () =>
    set((state) => ({
      selectionSession: {
        ...createInitialSelectionSession(),
        queryKey: state.selectionSession.queryKey
      }
    }))
}));
